import { Reveal, Overline } from "@/components/Kinetic";
import { useLang } from "@/i18n/LanguageContext";

const FACTS_EN = [
  { k: "Base", v: "Türkiye" },
  { k: "Founded", v: "August 2026" },
  { k: "Model", v: "Independent, product-owned" },
];

const FACTS_TR = [
  { k: "Merkez", v: "Türkiye" },
  { k: "Kuruluş", v: "Ağustos 2026" },
  { k: "Model", v: "Bağımsız, ürün sahibi" },
];

export default function AboutSection() {
  const { lang, tx } = useLang();
  const facts = lang === "tr" ? FACTS_TR : FACTS_EN;

  return (
    <section id="about" className="bg-paper py-20 sm:py-28 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-7">
            <Reveal>
              <Overline>{tx("Company", "Şirket")}</Overline>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-6 max-w-xl font-display text-3xl font-bold leading-tight tracking-tight text-ink sm:text-4xl">
                {tx(
                  "GADULABS is a product-focused software company based in Türkiye.",
                  "GADULABS, Türkiye merkezli, ürün odaklı bir yazılım şirketidir.",
                )}
              </h2>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600">
                {tx(
                  "We build intelligent digital products for international businesses — combining software engineering, applied AI and practical domain knowledge to turn complex workflows into focused software.",
                  "Uluslararası işletmeler için akıllı dijital ürünler geliştiriyoruz — yazılım mühendisliğini, uygulamalı yapay zekâyı ve pratik alan bilgisini birleştirerek karmaşık iş akışlarını odaklı yazılıma dönüştürüyoruz.",
                )}
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-600">
                {tx(
                  "Depending on the problem, our work takes different forms — web applications, SaaS products, mobile apps, corporate websites and AI-powered software. We primarily build and own software products, alongside selected digital initiatives for real operating businesses.",
                  "Soruna bağlı olarak çalışmalarımız farklı biçimler alır — web uygulamaları, SaaS ürünleri, mobil uygulamalar, kurumsal web siteleri ve yapay zekâ destekli yazılımlar. Önceliğimiz kendi yazılım ürünlerimizdir. Bunun yanında faal işletmeler için seçili dijital çalışmalar yürütürüz.",
                )}
              </p>
            </Reveal>
            <Reveal delay={0.32}>
              <dl className="mt-10 grid grid-cols-1 gap-6 border-t border-slate-200 pt-8 sm:grid-cols-3">
                {facts.map((f, i) => (
                  <div key={f.k} data-testid={`about-fact-${["base", "founded", "model"][i]}`}>
                    <dt className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">
                      {f.k}
                    </dt>
                    <dd className="mt-2 font-display text-base font-bold text-ink">
                      {f.v}
                    </dd>
                  </div>
                ))}
              </dl>
            </Reveal>
          </div>
          <Reveal delay={0.2} y={32} className="lg:col-span-5">
            <figure className="relative">
              <div className="overflow-hidden border border-slate-200">
                <img
                  src="https://images.unsplash.com/photo-1575318634028-6a0cfcb60c59?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwc29mdHdhcmUlMjBhcmNoaXRlY3R1cmUlMjBvZmZpY2UlMjBkZXNrJTIwdHlwb2dyYXBoeXxlbnwwfHx8fDE3ODg3ODY4NDZ8MA&ixlib=rb-4.1.0&q=85"
                  alt={tx("Minimal engineering workspace", "Minimal mühendislik çalışma alanı")}
                  loading="lazy"
                  className="aspect-[4/5] w-full object-cover grayscale transition-all duration-700 hover:grayscale-0 sm:aspect-[3/3.4]"
                />
              </div>
              <figcaption className="mt-3 flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">
                <span>{tx("Workspace study — illustrative image", "Çalışma alanı — temsili görsel")}</span>
                <span>Unsplash</span>
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
